import cx from 'clsx';
import * as React from 'react';
import { DeviceContext } from 'src/components/DeviceProvider/DeviceProvider';

import { DeviceOs } from '../../../logic/main/types';
import { JackpotLayerSearch, LiveTableLayerInfo } from '../../game-tile';
import ClosedOverlay from '../ClosedOverlay';
import { COMPRESSED_OUTFIT, SMALL_OUTFIT } from '../constants';
import GhostTile from '../GhostTile';
import ModularGridGameTile from '../ModularGridGameTile';
import { Info } from '../../icons';
import {
  GameClickProps,
  GameTileProps,
  GridSizeType,
  OverlayProps,
} from '../types';
import { prepareGameInfoTooltip, selectProperStyles } from '../utils';
import styles from './GameList.module.scss';
import { ShouldPreventDefaultForClick } from '../../LaunchStrategiesProvider/utils';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';
import GameLink from '../../GameLink';

interface GameListProps {
  gameList?: Array<GameTileProps>;
  onClick: (args: {
    gamePosition: number;
    gameId: string;
    game?: GameClickProps;
  }) => void;
  onInfoClick?: (args: {
    game?: GameClickProps;
    gamePosition: number;
    path: string;
  }) => void;
  insideHeader?: boolean;
  overlay?: OverlayProps;
  gamesLimit?: number;
  size?: GridSizeType;
  initialIndex?: number;
  locale: string;
  currency?: string;
  categoryAttributes?: CategoryFinancialWebAttributes;
  gameInfoTooltip: string;
}

const prepareGameTile = (item: GameTileProps) => {
  const { jackpot, table, ...rest } = item;
  return rest;
};

const RenderGameItem = (
  game: GameTileProps,
  position: number,
  handleClick: ({
    game,
    gamePosition,
    id,
  }: {
    game: GameClickProps;
    gamePosition: number;
    id: string;
  }) => void,
  locale: string,
  deviceType: 'mobile' | 'tablet' | 'desktop',
  deviceOs: DeviceOs,
  gameInfoTooltip: string,
  categoryAttributes?: CategoryFinancialWebAttributes,
  currency?: string,
  handleInfoClick?: ({
    game,
    gamePosition,
    path,
  }: {
    game: GameClickProps;
    gamePosition: number;
    path: string;
  }) => void
) => {
  const gameClickProps = {
    id: game.id,
    title: game.title,
    name: game.name,
    provider: game.provider,
    integration: game.integration,
    presentation_code: game.presentation_code,
    transaction_code: game.transaction_code,
    launchUrl: game.launchUrl,
    gameDetailsPath: game.gameDetailsPath,
    gameLaunchPath: game.gameLaunchPath,
    type: game.type,
    trackingTitle: game.tracking_metadata?.title || game.title,
    sash: game.sash,
  };

  const preventDefaultForGameClick = ShouldPreventDefaultForClick(
    game.integration
  );

  const handleGameListGameClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (preventDefaultForGameClick) e.preventDefault();
    handleClick({
      game: gameClickProps,
      gamePosition: position + 1,
      id: game.id,
    });
  };

  const handleGameListInfoIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleInfoClick &&
      handleInfoClick({
        game: gameClickProps,
        gamePosition: position + 1,
        path: game.gameDetailsPath,
      });
  };

  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    gameInfoTooltip,
    game.title
  );

  return (
    <div className={styles.slotWrapper}>
      <GameLink
        className={styles.slot}
        data-testid={`${position + 1}-slot`}
        onClick={handleGameListGameClick}
        target="_blank"
        dataTestId={`${position + 1}-slot`}
        url={game.launchUrl}
        categoryAttributes={categoryAttributes}
        integration={game.integration}
        notIndexed={game.notIndexed}
      >
        <div className={styles.image}>
          <ModularGridGameTile
            data-testid="modular-grid-tile"
            game={prepareGameTile(game)}
            outfit={SMALL_OUTFIT}
            isLazy
            categoryAttributes={categoryAttributes}
            gameInfoTooltip={preparedGameInfoTooltip}
          />
        </div>
        <div className={styles.mainInfo}>
          {game.type && (
            <p className={styles.description} data-testid="slot-description">
              {game.type}
            </p>
          )}
          <span className={styles.title} data-testid="slot-title">
            {game.title}
          </span>
          {!game.table && game.jackpot && (
            <JackpotLayerSearch {...game.jackpot} />
          )}
        </div>
        {game.table && (
          <LiveTableLayerInfo
            data-testid="life-table-layer-info"
            outfit={COMPRESSED_OUTFIT}
            stake={game.table.stake}
            players={game.table.players}
            locale={locale}
            currencyCode={currency}
          />
        )}
      </GameLink>
      {handleInfoClick && (
        <a
          className={styles.gameInfo}
          href={game.gameDetailsPath}
          target="_blank"
          rel={`noopener noreferrer ${game.notIndexed ? 'nofollow' : ''}`}
        >
          <div
            className={styles.infoWrapper}
            data-testid="game-info"
            onClick={handleGameListInfoIconClick}
          >
            <Info
              altText={preparedGameInfoTooltip}
              className={styles.infoIcon}
            />
          </div>
        </a>
      )}
    </div>
  );
};

const GameList: React.FC<GameListProps> = ({
  gameList = [],
  onClick,
  onInfoClick,
  insideHeader = true,
  overlay,
  size,
  gamesLimit,
  initialIndex = 0,
  locale,
  currency,
  categoryAttributes,
  gameInfoTooltip,
}) => {
  const { deviceType, deviceOs } = React.useContext(DeviceContext);
  if (!gameList.length && !overlay) return null;
  const handleClick = ({
    game,
    gamePosition,
    id,
  }: {
    game?: GameClickProps;
    gamePosition: number;
    id: string;
  }) => onClick({ gameId: id, game, gamePosition });

  const handleInfoClick = ({
    game,
    gamePosition,
    path,
  }: {
    game?: GameClickProps;
    gamePosition: number;
    path: string;
  }) => onInfoClick && onInfoClick({ game, gamePosition, path });

  const ghostItems = Array.from(Array(4).keys());

  const preparedGameList = gamesLimit
    ? gameList.slice(0, gamesLimit)
    : gameList;

  if (overlay) {
    return (
      <ul className={cx(styles.container, insideHeader && styles.insideHeader)}>
        {ghostItems.map(item => (
          <div className={styles.slot} key={item}>
            <div className={styles.image}>
              <GhostTile />
            </div>
          </div>
        ))}
        <ClosedOverlay {...overlay} />
      </ul>
    );
  }

  return (
    <ul
      className={cx(styles.container, insideHeader && styles.insideHeader, {
        [styles.containerMedium]: size === undefined,
        [styles.containerMediumForced]: size === 'medium',
      })}
      data-testid="game-list"
    >
      {preparedGameList.map((item: GameTileProps, position: number) => (
        <li
          key={`${item.id}-${position}`}
          className={cx(
            selectProperStyles(
              {
                small: styles.elementSmallForced,
                medium: styles.elementMediumForced,
                default: [styles.elementSmall, styles.elementMedium],
              },
              size
            ),
            {
              [styles.elementSmallLimitForced]: size === 'small' && gamesLimit,
              [styles.elementSmallLimit]: size === undefined && gamesLimit,
            }
          )}
        >
          {RenderGameItem(
            item,
            position + initialIndex,
            handleClick,
            locale,
            deviceType,
            deviceOs,
            gameInfoTooltip,
            categoryAttributes,
            currency,
            onInfoClick && handleInfoClick
          )}
        </li>
      ))}
    </ul>
  );
};
export default GameList;
